import { IconNotebook } from '@tabler/icons-react';

import ProgressBar from '@/components/ProgressBar';
import TagCapsule from '@/components/TagCapsule';
import {
  getCommonStatusLabel,
  getCommonStatusBadgeClass,
} from '@/constants/constant';
import { ECommonStatus } from '@/enum';
import { useFormatText } from '@/hooks/useFormatText';

interface IColorTheme {
  cardBg: string;
  iconBg: string;
}

const CLASS_COLOR_THEMES: IColorTheme[] = [
  { cardBg: 'bg-orange-100', iconBg: 'bg-orange-500' },
  { cardBg: 'bg-yellow-100', iconBg: 'bg-yellow-500' },
  { cardBg: 'bg-purple-100', iconBg: 'bg-purple-500' },
  { cardBg: 'bg-green-100', iconBg: 'bg-green-500' },
  { cardBg: 'bg-indigo-100', iconBg: 'bg-indigo-500' },
  { cardBg: 'bg-pink-100', iconBg: 'bg-fuchsia-500' },
];

interface IClassSummaryCardProps {
  className?: string;
  name: string;
  studentsCount: number;
  lessonsCount: number;
  colorIndex?: number;
  onClick?: () => void;
  assignedTo?: string;
  avgProgress?: number;
  status?: ECommonStatus;
}

const ClassSummaryCard = ({
  className = '',
  name,
  studentsCount,
  lessonsCount,
  colorIndex = 0,
  onClick,
  assignedTo,
  avgProgress,
  status,
}: IClassSummaryCardProps) => {
  const { formatText } = useFormatText();
  const theme = CLASS_COLOR_THEMES[colorIndex % CLASS_COLOR_THEMES.length];

  return (
    <div
      className={`${theme.cardBg} rounded-2xl p-6 flex flex-col gap-6 max-w-113.75 ${className}`}
      onClick={onClick}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className={`${theme.iconBg} p-2 rounded-lg shrink-0`}>
            <IconNotebook size={18} color="white" />
          </div>
          <h2 className="text-xl font-bold text-neutral-primary truncate">
            {name}
          </h2>
        </div>
        {status && (
          <TagCapsule
            value={formatText(getCommonStatusLabel(status))}
            className={getCommonStatusBadgeClass(status)}
          />
        )}
      </div>

      {assignedTo && (
        <div className="flex items-start justify-between gap-2">
          <span className="text-sm text-neutral-secondary whitespace-nowrap font-semibold">
            {formatText('teacher-classes.details.assigned-class-to')}
          </span>
          <span className="text-sm font-semibold text-neutral-primary text-end capitalize max-w-40 line-clamp-1">
            {assignedTo}
          </span>
        </div>
      )}

      {avgProgress !== undefined && (
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between">
            <span className="text-sm text-neutral-primary font-semibold">
              {formatText('teacher-classes.details.average-progress')}
            </span>
            <span className="text-sm font-semibold text-neutral-primary">
              {avgProgress}%
            </span>
          </div>
          <ProgressBar
            value={avgProgress}
            showPercentage={false}
            height="h-2"
          />
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        {/* Students Card */}
        <div className="bg-white rounded-lg px-4 py-2.5 flex flex-col items-center justify-center gap-1 shadow-sm">
          <span className="text-neutral-primary text-xs font-semibold tracking-wider">
            {formatText('teacher-classes.details.students')}
          </span>
          <span className="text-2xl font-semibold text-neutral-primary">
            {studentsCount}
          </span>
        </div>

        {/* Lessons Card */}
        <div className="bg-white rounded-lg px-4 py-2.5 flex flex-col items-center justify-center gap-1 shadow-sm">
          <span className="text-neutral-primary text-xs font-semibold tracking-wider">
            {formatText('teacher-classes.details.lessons')}
          </span>
          <span className="text-2xl font-semibold text-neutral-primary">
            {lessonsCount}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ClassSummaryCard;
